import * as React from 'react';
import { endSession, userSession, isSessionOrganizer } from 'state/session';
import { user } from 'utils/auth/user';
import Button from 'components/Button';
import { classNames } from 'utils/ui/css';
import { fireEvent } from 'utils/ui/app';
import DgaModal from 'components/DgaModals';
import { getCurrentDealerId } from 'utils/dgaUtils';
import { showToast } from 'state/toast';
import { showConfirmDialog } from 'state/confirm-dialog';
import { useLingui } from '@lingui/react/macro';

type TEndSessionButton = {
  className?: string;
  onEndSession?: () => void;
} & Omit<React.ComponentProps<typeof Button>, 'onClick' | 'icon' | 'className'>;

const EndSessionButton: React.FC<TEndSessionButton> = ({ svgClassName, className, onEndSession, ...buttonProps }) => {
  const session = userSession().useState();
  const [isDgaModalOpen, setIsDgaModalOpen] = React.useState<boolean>(false);
  const { t } = useLingui();

  const handleEndSession = ({ showDgaWarning = false }) => {
    // custom end session logic
    onEndSession?.();
    // default end session logic
    endSession();

    if (showDgaWarning) {
      showToast({
        title: t`Digital Giveaway not available`,
        message: t`Your account does not have the required dealer configuration to create a Digital Giveaway for this brand.`,
        type: 'warn',
        notificationId: 'WARN_DGA_NOT_AVAILABLE',
      });
    }
  };

  const handleNonOrganizerLeave = async () => {
    const confirmed = await showConfirmDialog({
      dialogId: 'DIALOG_END_SESSION_NON_ORGANIZER',
      title: t`Cannot end session`,
      message: t`Only the session organizer can end this session. However, you can leave this session and start a new one.`,
      confirmText: t`Leave and start new session`,
      cancelText: t`Stay in session`,
      'data-testid': 'non-organizer-leave-dialog',
    });

    if (confirmed) {
      // Start a whole new session
      await endSession({ leaveWithoutEnding: true });
    }
  };

  const endSessionCallback = async (e: React.MouseEvent) => {
    const canContinue = fireEvent({
      el: e.currentTarget as HTMLElement,
      name: 'e3d-stream-end',
      detail: { userType: user.type, session },
    });

    if (!canContinue) return;

    // Check if user is a non-organizer dealer
    if (user.isDealer && !isSessionOrganizer()) {
      // Show confirm dialog for non-organizer dealers
      await handleNonOrganizerLeave();
      return;
    }

    // Organizer flow continues as normal
    if (userSession().isSessionDgaEligible) {
      // Only dealers with a dealerId of the current brand can create a DGA
      if (getCurrentDealerId(session.brand!)) {
        setIsDgaModalOpen(true);
      } else {
        handleEndSession({ showDgaWarning: true });
      }
    } else {
      handleEndSession({});
    }
  };

  return (
    <>
      {userSession().customerCanSeeHangupButton && (
        <Button
          part={buttonProps.part ?? 'header-button--end-call'}
          data-testid="voip-button-end-call"
          className={classNames(className, 'stream-header-icon-end-call')}
          svgClassName={svgClassName}
          // keeping both icons in the non-shared case maintains the same design but adds another icon source
          icon="ex3d-icon--end-call"
          onClick={endSessionCallback}
          {...buttonProps}
        />
      )}
      <DgaModal isOpen={isDgaModalOpen} setIsOpen={setIsDgaModalOpen} />
    </>
  );
};

export default EndSessionButton;
